import { Service, Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap, defaultIfEmpty } from 'rxjs/operators';

@Service()
export class AlbumArtService {
	private http = inject(HttpClient);

	private apiKey = '9dad61e4552e68f25a22ef03d4b01db9';  // Replace with your actual API key
	private apiUrl = 'https://ws.audioscrobbler.com/2.0/';

	private clientId = 'c13df103a56d48e0a78877e91c6d662a';  // Use your actual client ID
	private clientSecret = '1a4795d470124b0a969d97cf259d8881';  // Use your actual client secret
	private tokenUrl = 'https://accounts.spotify.com/api/token';
	private searchUrl = 'https://api.spotify.com/v1/search';

	// Get access token from Spotify
	private getAccessToken(): Observable<string> {
		const headers = new HttpHeaders({
			'Content-Type': 'application/x-www-form-urlencoded',
			'Authorization': 'Basic ' + btoa(`${this.clientId}:${this.clientSecret}`)
		});

		const body = new HttpParams().set('grant_type', 'client_credentials');

		return this.http.post<{ access_token: string }>(this.tokenUrl, body.toString(), { headers })
			.pipe(
				map(response => response.access_token),
				catchError(error => {
					console.error('Error fetching Spotify access token:', error);
					return of(''); // Return an empty string instead of null
				})
			);
	}

	// Search for an artist by name to get their ID
	searchArtistByName(artistName: string): Observable<any> {
		return this.getAccessToken().pipe(
			switchMap(token => {
				if (!token) {
					throw new Error('Failed to fetch access token');
				}

				const headers = new HttpHeaders({
					'Authorization': `Bearer ${token}`
				});

				const params = new HttpParams()
					.set('q', artistName)
					.set('type', 'artist')
					.set('limit', '1'); // Limit to one result for simplicity

				return this.http.get<any>(this.searchUrl, { headers, params });
			}),
			map(data => {
				if (data.artists && data.artists.items.length > 0) {
					return data.artists.items[0]; // Return the first artist found
				}
				return null;
			}),
			catchError(error => {
				console.error('Error searching artist by name:', error);
				return of(null);
			})
		);
	}

	// Fetch artist image by artist object returned from search
	getArtistImageByName(artistName: string): Observable<string> {
		return this.searchArtistByName(artistName).pipe(
			map(artist => {
				if (artist && artist.images && artist.images.length > 0) {
					return artist.images[0].url; // Return the first (largest) image URL
				}
				return ''; // Return an empty string if no image is found
			}),
			catchError(error => {
				console.error('Error fetching artist image by name:', error);
				return of(''); // Return an empty string observable in case of an error
			})
		);
	}

	getAlbumArtFromLastFm(artist: string, album: string): Observable<string | null> {
		if (!artist || !album) {
			console.error('Artist and album names are required.');
			return of(null); // Return an observable of null if required params are missing
		}

		const params = {
			method: 'album.getinfo',
			api_key: this.apiKey,
			artist: artist,
			album: album,
			format: 'json'
		};

		return this.http.get(this.apiUrl, { params }).pipe(
			map((data: any) => {
				const images = data.album?.image;
				if (images && images.length > 0) {
					// Return the URL of the largest image
					return images[images.length - 1]['#text'];
				}
				return null; // Return null if no image is found
			}),
			catchError(error => {
				console.error('Error fetching album art from Last.fm:', error);
				return of(null); // Return null observable in case of error
			})
		);
	}

	getAlbumArt(artist: string, album?: string, size: 'small' | 'medium' | 'large' = 'large'): Observable<string | null> {
		return this.getAccessToken().pipe(
			switchMap(token => {
				if (!token) {
					throw new Error('Failed to fetch access token');
				}

				const headers = new HttpHeaders({
					'Authorization': `Bearer ${token}`
				});

				let query = artist.replace('&', 'and');
				if (album) {
					query += ` ${album}`;
				}

				const params = new HttpParams()
					.set('q', query)
					.set('type', album ? 'album' : 'artist')
					.set('limit', '1');

				return this.http.get<any>(this.searchUrl, { headers, params });
			}),
			map(response => {
				const items = response.artists?.items || response.albums?.items;
				if (!items || items.length === 0) {
					return null;
				}

				const images = items[0].images;
				if (!images || images.length === 0) {
					return null;
				}

				let smallest = images[0];
				let largest = images[0];

				for (const image of images) {
					if (image.width < smallest.width) {
						smallest = image;
					}
					if (image.width > largest.width) {
						largest = image;
					}
				}

				if (size === 'small') {
					return smallest.url;
				} else if (size === 'medium' && images.length > 1) {
					return images[1].url;
				} else {
					return largest.url; // Default to largest image
				}
			}),
			defaultIfEmpty(null), // Ensure that null is emitted if no items are found
			catchError(error => {
				console.error("Error fetching album art from Spotify:", error);
				console.warn("Trying Last.fm instead!");

				return this.getAlbumArtFromLastFm(artist, album || "").pipe(
					catchError(lastFmError => {
						console.error("Error fetching album art from Last.fm:", lastFmError);
						return of(null); // Return null observable in case of error
					})
				);
			})
		);
	}

	// Fetch Spotify song link by song title, artist name, and album name
	getSongLink(artistName: string, songTitle: string, albumName?: string, withImage?: boolean): Observable<string | [null, string] | null> {
		const trimmedArtist = this.sanitizeSearchTerm(artistName || '');
		const trimmedTitle = this.sanitizeSearchTerm(songTitle || '');
		const trimmedAlbum = this.sanitizeSearchTerm(albumName || '');

		if (!trimmedArtist || !trimmedTitle) {
			// console.warn('[NowPlaying] Spotify search skipped (missing data)', {
			// 	artist: artistName,
			// 	title: songTitle,
			// 	album: albumName
			// });
			return of(null);
		}

		const query = `track:"${trimmedTitle}" artist:"${trimmedArtist}"`;

		return this.getAccessToken().pipe(
			switchMap(token => {
				if (!token) {
					throw new Error('Failed to fetch access token');
				}

				const headers = new HttpHeaders({
					'Authorization': `Bearer ${token}`
				});

				const market = this.resolveMarket();
				return this.searchTracks(query, headers, market).pipe(
					defaultIfEmpty([])
				);
			}),
			map(tracks => {
				if (!tracks.length) {
					// console.warn('[NowPlaying] Spotify search returned no results', { query });
					return null; // Return null if no track is found
				}

				const topTrack = tracks[0];
				const artistNames = topTrack.artists?.map((artist: { name: string }) => artist.name).filter(Boolean).join(', ') || '';
				// console.warn('[NowPlaying] Spotify search match', {
				// 	query,
				// 	track: topTrack.name,
				// 	artists: artistNames,
				// 	album: topTrack.album?.name || '',
				// 	url: topTrack.external_urls?.spotify || ''
				// });
				if (withImage && topTrack.album?.images?.length) {
					const images = topTrack.album.images;
					const preferred = images[1]?.url || images[0]?.url || '';
					return [topTrack.external_urls.spotify, preferred]; // Return the Spotify link + album image
				} else {
					return topTrack.external_urls.spotify; // Return the Spotify link to the track
				}
			}),
			catchError(error => {
				console.error("Error fetching Spotify song link:", error);
				return of(null);
				// console.warn("Trying Last.fm instead!", artistName, songTitle, albumName);

				// return this.getAlbumArtFromLastFm(artistName, albumName  ?? "").pipe(
				// 	map(lastFmLink => lastFmLink ? [null, lastFmLink] as [null, string] : null), // Explicitly type as [null, string]
				// 	catchError(lastFmError => {
				// 		console.error("Error fetching album art from Last.fm:", lastFmError);
				// 		return of(null); // Return null if Last.fm also fails
				// 	})
				// );
			})
		);
	}

	private resolveMarket(): string | null {
		if (typeof navigator === 'undefined') return 'US';
		const language = navigator.language || '';
		const region = language.split('-')[1];
		return (region || 'US').toUpperCase();
	}

	private sanitizeSearchTerm(value: string): string {
		return value.replace(/"/g, '').replace(/\s+/g, ' ').trim();
	}

	private searchTracks(query: string, headers: HttpHeaders, market: string | null): Observable<any[]> {
		let params = new HttpParams()
			.set('q', query)
			.set('type', 'track')
			.set('limit', '1'); // Limit to one result for simplicity

		if (market) params = params.set('market', market);

		return this.http.get<any>(this.searchUrl, { headers, params }).pipe(
			map(response => response.tracks?.items || []),
		);
	}
}
